import { useEffect, useState } from "react";
import { api } from "../../api/client";
import type { InventorySheetRow } from "../../api/types";
import { useToasts } from "../../state/toasts";
import { Empty } from "../../ui/components";
import { money } from "../../ui/format";
import { formatQuantity, UNIT_TITLES } from "../../ui/units";

/**
 * Инвентаризация: факт против расчёта.
 *
 * Расчётный остаток показывается справочно, а вводится только факт: сервер
 * пересчитает разницу в момент проведения. Между печатью ведомости и вводом
 * цифр точка продолжает продавать, и принять расчёт из браузера значило бы
 * списать этот расход второй раз как недостачу.
 */
export function InventorySheet({ manage, onSaved }: { manage: boolean; onSaved: () => Promise<void> }) {
  const { run, notify } = useToasts();

  const [rows, setRows] = useState<InventorySheetRow[]>([]);
  const [actual, setActual] = useState<Record<string, string>>({});
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;

    api
      .get<{ rows: InventorySheetRow[] }>("/api/admin/stock/inventory/sheet")
      .then(data => {
        if (!cancelled) setRows(data.rows);
      })
      .catch(error => {
        if (!cancelled) notify(error instanceof Error ? error.message : "Не удалось загрузить ведомость", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [notify]);

  const counted = rows.filter(row => actual[row.id] !== undefined && actual[row.id] !== "");

  async function submit() {
    setBusy(true);
    const ok = await run(
      () =>
        api.once.post("/api/admin/stock/inventories", {
          comment: comment.trim() || undefined,
          counts: counted.map(row => ({
            ingredientId: row.id,
            quantity: Number(actual[row.id]),
            unit: row.unit
          }))
        }),
      "Инвентаризация проведена"
    );

    setBusy(false);
    if (ok) {
      setActual({});
      setComment("");
      await onSaved();
    }
  }

  if (loading) return <Empty>Загрузка ведомости…</Empty>;
  if (rows.length === 0) return <Empty>Номенклатура пуста — пересчитывать нечего.</Empty>;

  const difference = counted.reduce((sum, row) => {
    const value = Number(actual[row.id]);
    return sum + (value - row.expected) * row.avgCost;
  }, 0);

  return (
    <section className="panel">
      <header>
        <div>
          <h2>Пересчёт остатков</h2>
          <p>
            Заполняйте только те позиции, которые пересчитали: пустая строка в документ не попадёт. Расчётный остаток
            сервер возьмёт заново в момент проведения — то, что продалось, пока вы считали, недостачей не станет.
          </p>
        </div>
        {manage ? (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={counted.length === 0 || busy}
            onClick={() => void submit()}
          >
            {busy ? "Проводим…" : `Провести (${counted.length})`}
          </button>
        ) : null}
      </header>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Позиция</th>
              <th className="num">Расчётный остаток</th>
              <th className="num">Факт</th>
              <th>Единица</th>
              <th className="num">Разница</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const raw = actual[row.id];
              const entered = raw !== undefined && raw !== "";
              const diff = entered ? Number(raw) - row.expected : 0;

              return (
                <tr key={row.id} className={entered && diff !== 0 ? "row-alert" : ""}>
                  <td>
                    <strong>{row.title}</strong>
                    <br />
                    <small className="muted">{row.groupTitle ?? "без группы"}</small>
                  </td>
                  <td className="num">{formatQuantity(row.expected, row.unit)}</td>
                  <td className="num">
                    <input
                      type="number"
                      min={0}
                      step="0.001"
                      disabled={!manage}
                      value={raw ?? ""}
                      onChange={event =>
                        setActual(current => ({ ...current, [row.id]: event.target.value }))
                      }
                    />
                  </td>
                  <td>{UNIT_TITLES[row.unit]}</td>
                  <td className="num">
                    {entered && diff !== 0 ? (
                      <>
                        {diff > 0 ? "+" : ""}
                        {formatQuantity(diff, row.unit)}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {manage ? (
        <>
          <label className="field">
            <span>Комментарий</span>
            <input value={comment} onChange={event => setComment(event.target.value)} maxLength={500} />
          </label>

          {counted.length > 0 ? (
            <p className="hint">
              Расхождение по введённым строкам: <strong>{money(difference)}</strong>. Точная сумма считается сервером
              по себестоимости на момент проведения.
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
